import { TransformNode, Vector3 } from '@babylonjs/core';
import { BabylonjsModule, getQualitySettings, Resolution } from '@modules/babylonjs';
import { DeviceTier } from '@modules/babylonjs/src/DeviceInfo';
import { Injectable, Zed } from '@modules/core';
import {
  getTimer,
  GraphicAPI,
  onAllModelLoaded,
  PostprocessOptions,
  RenderManager,
  StandardZed,
  Timer,
  ZEDCamera,
} from '@modules/standard';
import { World } from 'miniplex';

import DefaultPostprocessOptions from './assets/postprocess/default.json';
import { TourDanjiCameraAPI } from './camera/TourDanjiCameraAPI';
import { MAX_UNITS_FOR_HIGH_QUALITY } from './Constants';
import { MeshType } from './ecs/component/data/DanjiMesh';
import { RoomTypeResponse } from './ecs/component/response/RoomType';
import { UnitResponse } from './ecs/component/response/Unit';
import { Entity } from './ecs/Entity';
import { DanjiMeshSystem } from './ecs/system/DanjiMeshSystem';
import { DongSystem } from './ecs/system/DongSystem';
import { FacilitySystem } from './ecs/system/FacilitySystem';
import { GroundSystem } from './ecs/system/GroundSystem';
import { ItemHoSystem } from './ecs/system/ItemHoSystem';
import { RaycastSystem } from './ecs/system/RaycastSystem';
import { SunSystem } from './ecs/system/SunSystem';
import { TreeSystem } from './ecs/system/TreeSystem';
import { UnitModelSystem } from './ecs/system/UnitModelSystem';
import { UnitSystem } from './ecs/system/UnitSystem';
import { MaterialManager } from './graphic/material/MaterialManager';
import { LightManager } from './LightManager';
import {
  onDongLoaded,
  onFacilityLoaded,
  onGroundLoaded,
  onPreLoad,
  onTreeLoadFailed,
  onUnitViewStateChanged,
  onUpdateRoomTypeFilter,
} from './observable/TourDanjiObservable';
import { TourDanjiFetcher } from './TourDanjiFetcher';
import { TOURDANJI_MODULE_OPTIONS_TOKEN, TourDanjiMetaData, TourDanjiOptions } from './TourDanjiModule';
import { isEmptyOrWhitespace } from './utiles/Utils';
export type ItemFocusOptions = {
  itemId: number;
};
export type HoFocusOptions = {
  hoId: number;
};
export type DongHoFoucsOptions = {
  dong: string;
  ho: string;
};
export type FocusOptions = ItemFocusOptions | HoFocusOptions | DongHoFoucsOptions;

export type TierGraphicQuality = 'high' | 'mobile-high' | 'medium' | 'low';

const TierGraphicQuality: Record<DeviceTier, TierGraphicQuality> = {
  [DeviceTier.Tier1]: 'high',
  [DeviceTier.Tier1_5]: 'mobile-high',
  [DeviceTier.Tier2]: 'medium',
  [DeviceTier.Tier3]: 'low',
  [DeviceTier.Unknown]: 'high',
} as const;

export class TourDanjiZed extends StandardZed implements Injectable {
  static readonly INJECTABLE_TOKEN = Symbol.for('TourDanjiZed');

  private readonly danjiOptions;

  private readonly cameraAPI;
  private readonly lightManager;
  private readonly danjiFetcher;
  private readonly danjiMeshSystem;
  private readonly groundSystem;
  private readonly dongSystem;
  private readonly itemHoSystem;
  private readonly facilitySystem;
  private readonly treeSystem;
  private readonly raycastSystem;
  private readonly sunSystem;
  private readonly unitModelSystem;
  private readonly unitSystem;
  private rootTransform!: TransformNode;

  constructor({
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: danjiOptions,

    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: cameraAPI,
    [LightManager.INJECTABLE_TOKEN]: lightManager,
    [RenderManager.INJECTABLE_TOKEN]: renderManager,
    [GraphicAPI.INJECTABLE_TOKEN]: graphicAPI,
    [TourDanjiFetcher.INJECTABLE_TOKEN]: danjiFetcher,
    [GroundSystem.INJECTABLE_TOKEN]: groundSystem,
    [DongSystem.INJECTABLE_TOKEN]: dongSystem,
    [ItemHoSystem.INJECTABLE_TOKEN]: itemHoSystem,
    [FacilitySystem.INJECTABLE_TOKEN]: facilitySystem,
    [TreeSystem.INJECTABLE_TOKEN]: treeSystem,
    [RaycastSystem.INJECTABLE_TOKEN]: raycastSystem,
    [SunSystem.INJECTABLE_TOKEN]: sunSystem,
    [UnitModelSystem.INJECTABLE_TOKEN]: unitModelSystem,
    [UnitSystem.INJECTABLE_TOKEN]: unitSystem,
    [DanjiMeshSystem.INJECTABLE_TOKEN]: danjiMeshSystem,
  }: {
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: TourDanjiOptions;

    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: TourDanjiCameraAPI;
    [LightManager.INJECTABLE_TOKEN]: LightManager;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
    [RenderManager.INJECTABLE_TOKEN]: RenderManager;
    [GraphicAPI.INJECTABLE_TOKEN]: GraphicAPI;
    [TourDanjiFetcher.INJECTABLE_TOKEN]: TourDanjiFetcher;
    [GroundSystem.INJECTABLE_TOKEN]: GroundSystem;
    [DongSystem.INJECTABLE_TOKEN]: DongSystem;
    [ItemHoSystem.INJECTABLE_TOKEN]: ItemHoSystem;
    [FacilitySystem.INJECTABLE_TOKEN]: FacilitySystem;
    [TreeSystem.INJECTABLE_TOKEN]: TreeSystem;
    [RaycastSystem.INJECTABLE_TOKEN]: RaycastSystem;
    [SunSystem.INJECTABLE_TOKEN]: SunSystem;
    [UnitModelSystem.INJECTABLE_TOKEN]: UnitModelSystem;
    [UnitSystem.INJECTABLE_TOKEN]: UnitSystem;
    [DanjiMeshSystem.INJECTABLE_TOKEN]: DanjiMeshSystem;
  }) {
    super(world, babylonjs.scene, babylonjs.engine, babylonjs.options, graphicAPI, false, renderManager);

    this.danjiOptions = danjiOptions;

    this.cameraAPI = cameraAPI;
    this.lightManager = lightManager;
    this.danjiFetcher = danjiFetcher;
    this.dongSystem = dongSystem;
    this.itemHoSystem = itemHoSystem;
    this.groundSystem = groundSystem;
    this.facilitySystem = facilitySystem;
    this.treeSystem = treeSystem;
    this.raycastSystem = raycastSystem;
    this.sunSystem = sunSystem;
    this.unitModelSystem = unitModelSystem;
    this.unitSystem = unitSystem;
    this.danjiMeshSystem = danjiMeshSystem;

    this.lightManager.initializeLight();
    this.initializeAPIs();

    this.cameraAPI.setGetDongEntityCallback((buildingId: number) => {
      const dongEntity = this.dongSystem.getDongEntity(buildingId);
      return dongEntity || null;
    });
  }
  public override dispose() {
    super.dispose();
  }

  private timer?: Timer;
  public loadDanji = async (danjiId: string) => {
    const loadingStartTime = Date.now();
    this.timer = getTimer(`load(${danjiId})`);
    this.timer.reset();

    this.cameraAPI.enableControl(false);

    this.rootTransform = new TransformNode(danjiId);
    this.rootTransform.scaling = new Vector3(1, 1, -1);

    // 단지 데이터 preload
    await this.preload(danjiId);
    // 그래픽 설정
    await this.applyGraphicSettings();

    // 단지 지형 생성 (GIS Ground + Decals)
    await this.generateGround(danjiId);

    // 단지 시설물 생성
    await this.generateFacility();
    this.renderManager.renderUpdate();

    if (!this.danjiOptions.disableDecal) {
      await this.generateDecals();
    }

    // 단지 동 생성
    await this.generateDong(danjiId);
    this.renderManager.renderUpdate();

    const hoId = this.selectItem(this.danjiOptions);
    this.setDanjiCamera(hoId);
    this.timer?.print('generateDong / setDanjiView');

    // 단지 나무 생성 (among the facilities, only trees)
    await this.generateTree();
    this.renderManager.renderUpdate();

    // Tier3는 1frame만 렌더링 후 렌더링 안함
    if (this.justOneRenderLoop) {
      this.scene.onReadyObservable.addOnce(() => {
        const targetFrames = 5;
        let frames = 0;
        const afterRender = this.scene.onAfterRenderObservable.add(() => {
          if (frames++ < targetFrames) return;
          this.renderManager.stopRenderLoop();
          afterRender.remove();
        });
      });
      this.cameraAPI.enableControl(false);
    } else {
      this.renderManager.forceRender = true;
    }

    onAllModelLoaded.notifyObservers();

    const targetItem = this.itemHoSystem.getTargetEntity();

    return {
      ...this.danjiOptions.zamParams,
      danjiId: danjiId,
      itemId: this.danjiOptions.itemId?.toString() || '',
      dong: targetItem?.ItemHo.meta.동,
      ho: targetItem?.ItemHo.meta.호,
      deviceTier: this.options.deviceTier,
      gpuModel: this.options.deviceInfo?.gpuModel,
      loadingTime: (Date.now() - loadingStartTime) / 1000,
      disableInteriorView: this.danjiOptions.disableInteriorView,
    } as TourDanjiMetaData;
  };

  public unloadDanji = async (_danjiId: string) => {
    this.rootTransform.dispose(false, true);
  };

  public updateRoomTypeResponse(responses: RoomTypeResponse[]) {
    responses.forEach((response) => {
      this.dongSystem.setVisibleRoomTypeWall(response.roomTypeId, response.active);
    });
    if (onUpdateRoomTypeFilter.hasObservers()) {
      onUpdateRoomTypeFilter.notifyObservers(responses);
    }
  }

  public setVisibleMeshes(meshType: MeshType, isVisible: boolean) {
    this.dongSystem.setVisibleMeshes(meshType, isVisible);
  }

  public async pushCameraView(...args: Parameters<TourDanjiCameraAPI['pushCameraView']>) {
    return this.cameraAPI.pushCameraView(...args);
  }

  public popCameraView() {
    this.cameraAPI.popCameraView();
  }

  public getUnitsInDong(buildingId: number) {
    return this.danjiFetcher.dongs
      ?.find((dong) => dong.areaBuildingId === buildingId)
      ?.building.hos.filter((ho) => !isEmptyOrWhitespace(ho.meta.호))
      .map((ho) => {
        return {
          hoId: ho.meta.ho_id,
          hoName: ho.meta.호,
        };
      });
  }

  public selectDong(dongId: number) {
    this.dongSystem.selectDong(dongId);
  }

  public unselectDong() {
    this.dongSystem.unselectDong();
  }

  private static readonly TIER3_RENDER_FRAMES = 5;

  public async setTreeVisible(isVisible: boolean): Promise<void> {
    // Abort any in-progress tree loading when toggling off
    if (!isVisible && this.treeLoadAbortController) {
      this.treeLoadAbortController.abort();
      this.treeLoadAbortController = null;
    }

    // If trees not loaded yet (check via ECS), load them first
    const needsLazyLoad = !this.isTreeSystemLoaded() && isVisible;
    if (needsLazyLoad) {
      // loadTreeSystemLazy throws AbortError if cancelled — let it propagate
      // so the caller (TreePanel.handleToggle) can skip setting visibility state.
      await this.loadTreeSystemLazy();
    }

    this.danjiMeshSystem.setVisible(MeshType.FACILITY_TREE_NORMAL, isVisible);
    this.danjiMeshSystem.setVisible(MeshType.FACILITY_TREE_BUSH, isVisible);
    this.danjiMeshSystem.setVisible(MeshType.FACILITY_TREE_FLOWER, isVisible);

    if (needsLazyLoad && this.justOneRenderLoop) {
      // Tier3: render loop is stopped after initial load. ThinInstance GPU buffers
      // need multiple frames to be fully committed, so render several frames then stop.
      this.renderManager.runRenderLoop();
      let frames = 0;
      const afterRender = this.scene.onAfterRenderObservable.add(() => {
        if (frames++ < TourDanjiZed.TIER3_RENDER_FRAMES) return;
        this.renderManager.stopRenderLoop();
        afterRender.remove();
      });
    } else {
      this.renderManager.renderUpdate();
    }
  }

  public getDefaultTreeVisibility(): boolean {
    return this.defaultTreeVisible;
  }

  public isTreeSystemLoaded(): boolean {
    // Leverage ECS: check if tree entities exist in the world
    return this.treeSystem.hasTreeEntities();
  }

  private disableTree = false;
  private defaultTreeVisible = true;
  private treeLoadAbortController: AbortController | null = null;
  private treeLoadPromise: Promise<void> | null = null;
  private disableFacility = false;
  private renderingItemDongOnly = false;
  private exceptRoofTop = false;
  private justOneRenderLoop = false;

  private async applyGraphicSettings() {
    const quality = this.checkGraphicQuality();
    let targetResolution = getQualitySettings().resolution;
    let postprocessOptions = DefaultPostprocessOptions as PostprocessOptions;

    switch (quality) {
      case 'high':
        this.defaultTreeVisible = true;
        break;
      case 'mobile-high':
        this.exceptRoofTop = true;
        this.defaultTreeVisible = false;
        break;
      case 'medium':
        this.exceptRoofTop = true;
        this.defaultTreeVisible = false;
        targetResolution = Math.min(targetResolution, Resolution.p900);
        break;
      case 'low':
        this.exceptRoofTop = true;
        this.justOneRenderLoop = true;
        this.defaultTreeVisible = false;

        targetResolution = Math.min(targetResolution, Resolution.p720);

        this.scene.shadowsEnabled = false;
        break;
    }

    if (targetResolution !== undefined) this.graphic.setResolution(targetResolution);
    if (postprocessOptions !== undefined) this.graphic.updatePostprocessOptions(postprocessOptions);

    this.timer?.print('applyGraphicSettings');
  }

  private async preload(danjiId: string) {
    await Promise.all([
      this.danjiFetcher.fetchDecal(danjiId),
      this.danjiFetcher.fetchDanji(danjiId),
      this.danjiFetcher.fetchItemIds(danjiId),
      this.danjiFetcher.fetchRaw2dFacilityData(danjiId),
      this.danjiFetcher.fetchRaw3dFacilityData(danjiId),
    ]);

    onPreLoad.notifyObservers();

    this.timer?.print('preload');
  }

  private async generateGround(danjiId: string) {
    await this.groundSystem.generateGround(danjiId, this.rootTransform);
    this.timer?.print('generateGround / generateGround');

    // 단지 지형에 카메라 맞춤
    this.fitBounding();
    this.timer?.print('generateGround / fitBounding');

    onGroundLoaded.notifyObservers();
  }

  private async generateDecals() {
    const decalTransform = new TransformNode('Decals');
    decalTransform.setParent(this.rootTransform);

    await Promise.all([this.groundSystem.generateDecals(decalTransform)]);
    this.timer?.print('generateGround / generateDecals');
  }

  private async generateDong(danjiId: string) {
    const renderingItemDongOnly = this.danjiOptions.renderingItemDongOnly || this.renderingItemDongOnly;
    // 단지 동 데이터
    await this.danjiFetcher.fetchDong(danjiId, renderingItemDongOnly, this.exceptRoofTop);
    this.timer?.print('generateDong / fetchDong');

    // 단지 동 생성
    const dongTransform = new TransformNode('Dong');
    dongTransform.setParent(this.rootTransform);
    await Promise.all([
      this.dongSystem.generateDongs(dongTransform, renderingItemDongOnly, this.exceptRoofTop),
      this.scene.whenReadyAsync(false),
    ]);

    this.timer?.print('generateDong / generateDong');

    onDongLoaded.notifyObservers();
  }

  private async generateFacility() {
    if (this.disableFacility || this.danjiOptions.disableFacility) return;

    // 단지 시설물 생성 전처리
    this.danjiFetcher.convert3DFacilityData();
    this.timer?.print('generateFacility / convert3DFacilityData');

    // 단지 시설물 생성
    const facilityTransform = new TransformNode('Facility');
    facilityTransform.setParent(this.rootTransform);
    this.facilitySystem.generateFacilityBuilding(facilityTransform);
    this.timer?.print('generateFacility / generateFacilityBuilding');

    // Convert 2D facility data after 3D building generation for correct positioning
    this.danjiFetcher.convert2DFacilityData();
    this.timer?.print('generateFacility / convert2DFacilityData');

    this.facilitySystem.generateGym(facilityTransform);
    this.facilitySystem.generateParking(facilityTransform);
    await this.facilitySystem.generateParkingEnterance(facilityTransform);
    this.timer?.print('generateFacility / generate2DFacilities');

    onFacilityLoaded.notifyObservers();
  }

  private async generateTree() {
    if (this.disableTree || this.danjiOptions.disableTree) return;

    // Skip loading on lower tiers where trees are hidden by default
    if (!this.defaultTreeVisible) {
      console.log('[TreeSystem] Deferring tree loading for lower tier device');
      return;
    }

    const treeTransform = new TransformNode('trees');
    treeTransform.setParent(this.rootTransform);

    await this.treeSystem.preloadTreeAssets(treeTransform);
    this.timer?.print('generateTree / preloadTreeAssets');

    await Promise.all([this.treeSystem.generateTrees(this.defaultTreeVisible), this.scene.whenReadyAsync(false)]);
    this.timer?.print('generateTree / generateTrees');

    // No need to track state - ECS entities will exist after this
  }

  private static lazyLoadCallId = 0;

  private async loadTreeSystemLazy(): Promise<void> {
    const callId = ++TourDanjiZed.lazyLoadCallId;

    // Check ECS state instead of boolean flag
    if (this.isTreeSystemLoaded()) { return; }
    if (this.disableTree || this.danjiOptions.disableTree) { return; }

    // Abort any previous in-progress loading and wait for its cleanup to finish.
    // Without this, the previous call's deferred cleanup (disposeGeneratedInstances)
    // can race with the new call's preloadTreeAssets, wiping out freshly loaded assets.
    if (this.treeLoadAbortController) {
      this.treeLoadAbortController.abort();
    }
    const prevPromise = this.treeLoadPromise;
    if (prevPromise) {
      await prevPromise.catch(() => {});
      // Re-check: previous load might have succeeded before abort took effect
      if (this.isTreeSystemLoaded()) { return; }
    }

    const abortController = new AbortController();
    this.treeLoadAbortController = abortController;

    const treeTransform = new TransformNode('trees');
    treeTransform.setParent(this.rootTransform);

    const loadPromise = (async () => {
      await this.treeSystem.preloadTreeAssets(treeTransform);

      if (abortController.signal.aborted) {
        this.treeSystem.disposeGeneratedInstances();
        treeTransform.dispose();
        throw new DOMException('Tree loading was aborted.', 'AbortError');
      }

      await this.treeSystem.generateTrees(true, abortController.signal);
      // The scene should already be ready from preloadTreeAssets via gltfLoader's autoCompileShader.
      // Calling whenReadyAsync again here can sometimes hang if a previous call was aborted.

      if (abortController.signal.aborted) {
        this.treeSystem.disposeGeneratedInstances();
        treeTransform.dispose();
        throw new DOMException('Tree loading was aborted.', 'AbortError');
      }
    })();

    this.treeLoadPromise = loadPromise;

    try {
      await loadPromise;
    } catch (error) {
      const isAbort = error instanceof DOMException && error.name === 'AbortError';
      // AbortError is expected when user cancels — just re-throw without error UI
      if (isAbort) {
        throw error;
      }
      console.error('[TreeSystem] loadTreeSystemLazy failed:', error);
      // Cleanup any partially created instances and the parent transform
      this.treeSystem.disposeGeneratedInstances();
      treeTransform.dispose();
      // Notify UI so TreePanel can show error state and auto-toggle off
      onTreeLoadFailed.notifyObservers({
        reason: error instanceof Error ? error.message : 'Unknown error during tree loading',
      });
      throw error; // Re-throw so TreePanel's catch block also handles it
    } finally {
      const ownsController = this.treeLoadAbortController === abortController;
      const ownsPromise = this.treeLoadPromise === loadPromise;
      // Only clear shared fields if THIS call still owns them.
      // A retry call may have already replaced them — don't overwrite.
      if (ownsController) {
        this.treeLoadAbortController = null;
      }
      if (ownsPromise) {
        this.treeLoadPromise = null;
      }
    }
  }

  private fitBounding() {
    const camera = this.scene.activeCamera;
    if (camera && camera instanceof ZEDCamera) {
      const boundSize = this.groundSystem.cameraBoundingSize;
      const boundOffset = this.groundSystem.cameraBoundingOffset;

      camera.setTarget(
        Vector3.Clamp(
          camera.target,
          new Vector3(-boundSize + boundOffset.x, 0, -boundSize + boundOffset.z),
          new Vector3(boundSize + boundOffset.x, boundSize * 3, boundSize + boundOffset.z)
        )
      );
    }
  }
  private selectItem(options: TourDanjiOptions): number | undefined {
    let unitId: number | undefined = options.itemHoId ? Number.parseInt(options.itemHoId) : undefined;
    let dongId: number | undefined = options.itemDongId ? Number.parseInt(options.itemDongId) : undefined;

    if (this.danjiOptions.itemId) {
      const item = this.danjiFetcher.getItem(this.danjiOptions.itemId);
      unitId = item?.areaHoId;
      dongId = item?.areaBuildingId;
    }
    if (dongId) {
      this.dongSystem.selectDong(dongId);
    }
    if (unitId) {
      this.dongSystem.getDongEntity(unitId);
    }
    this.itemHoSystem.setTargetItem(unitId);
    return unitId;
  }

  private setDanjiCamera(hoId?: number) {
    const camera = this.scene.activeCamera;
    if (!(camera instanceof ZEDCamera)) return;

    let target = Vector3.ZeroReadOnly;
    let direction = new Vector3(0, 6, -4).normalize();
    let radius = this.dongSystem.getDefaultRadius();
    camera.lowerRadiusLimit = this.dongSystem.getMinRadius();
    camera.upperRadiusLimit = this.dongSystem.getMaxRadius();

    let heightOffset = this.dongSystem.getZoomPivotHeight();

    const buildingId = hoId ? this.danjiFetcher.getBuildingByHoId(hoId)?.building?.meta.bd_id : undefined;
    const building = this.danjiFetcher.renderData?.dongRenderDatas.find((x) => x.areaBuildingId === buildingId);
    const boundingBox = building?.boundingInfo.boundingBox;
    if (boundingBox) {
      const position = boundingBox.center;
      target = new Vector3(position.x, 0, position.z);
      heightOffset = (boundingBox.minimum.y + boundingBox.maximum.y) * 0.7;
    } else {
      const danjiCenter = this.danjiFetcher.renderData?.boundingInfo.boundingBox.center;
      if (danjiCenter) {
        const groundCenter = this.raycastSystem.pickWithRayByLand(danjiCenter.x, danjiCenter.y);
        const center = groundCenter?.pickedPoint ?? Vector3.ZeroReadOnly;
        target = center;
      }
    }
    target.addInPlace(Vector3.UpReadOnly.scale(heightOffset));
    camera.setTarget(target.clone());
    camera.targetHeightPivot = target.y;
    camera._setPosition(target.add(direction.scale(radius)));

    this.cameraAPI.applyPresetInputs('DanjiView');
    this.cameraAPI.enableControl(true);
  }

  public focus(options: FocusOptions) {
    const camera = this.scene.activeCamera;
    if (!camera || !(camera instanceof ZEDCamera)) {
      console.error(new Error('Invalid Camera'));
      return;
    }

    const hoId = ((options: FocusOptions) => {
      if ('hoId' in options) {
        return options.hoId;
      }
      if ('itemId' in options) {
        const item = this.danjiFetcher.getItem(options.itemId);
        return item?.areaHoId;
      }
      if ('dong' in options && 'ho' in options) {
        const buildingName = options.dong.replace(/동$/, '');
        const unitName = options.ho.replace(/호$/, '');
        const dong = this.danjiFetcher.dongs?.find((dong) => dong.name.replace(/동$/, '') === buildingName);
        if (!dong) {
          console.error(new Error('해당 동이 존재하지 않습니다.'), buildingName, this.danjiFetcher.dongs);
          return;
        }

        const ho = dong.building.hos.find((ho) => ho.meta.호.replace(/호$/, '') === unitName);
        if (!ho) {
          console.error(new Error(`해당 호가 존재하지 않습니다. dong : ${buildingName}, ho : ${unitName}`));
          return;
        }

        return ho.meta.ho_id;
      }

      return;
    })(options);

    const dongId = ((options: FocusOptions) => {
      if ('itemId' in options) {
        const item = this.danjiFetcher.getItem(options.itemId);
        return item?.areaBuildingId;
      }
      if ('dong' in options) {
        const buildingName = options.dong.replace(/동$/, '');
        const dong = this.danjiFetcher.dongs?.find((dong) => dong.name.replace(/동$/, '') === buildingName);
        if (!dong) {
          console.error(new Error('해당 동이 존재하지 않습니다.'), buildingName, this.danjiFetcher.dongs);
          return;
        }
        return dong.areaBuildingId;
      }

      return;
    })(options);

    this.selectItem({
      itemDongId: dongId?.toString(),
      itemHoId: hoId?.toString(),
    } as TourDanjiOptions);
    this.setDanjiCamera(hoId);
  }

  public getCurrentDate() {
    return this.sunSystem.getCurrentDate();
  }

  public changeUnitFloor(floorIndex: number) {
    this.unitModelSystem.changeFloor(floorIndex);
  }

  private checkGraphicQuality(): TierGraphicQuality {
    let quality = TierGraphicQuality[this.options.deviceTier ?? DeviceTier.Unknown];

    if (['high', 'mobile-high'].includes(quality)) {
      const totalUnits =
        this.danjiFetcher.danjiData?.dongs?.reduce((total, dong) => {
          return total + dong.세대수;
        }, 0) ?? 0;
      if (totalUnits > MAX_UNITS_FOR_HIGH_QUALITY) {
        quality = 'medium';
      }
    }

    return quality;
  }

  public getCameraType() {
    return this.cameraAPI.getCameraType();
  }

  public enterUnit(dong: string, hoName: string) {
    const unitData = this.danjiFetcher.dongs
      ?.find((v) => v.name === dong)
      ?.building.hos.find((ho) => ho.meta.호 === hoName);
    if (!unitData) {
      console.error(new Error('해당 동호수가 존재하지 않습니다.'), dong, hoName);
      return;
    }
    this.unitModelSystem.enter(unitData);
  }

  public async enterUnitById(unitId: number, isHideUnitName?: boolean) {
    const unitData = this.danjiFetcher.getUnitData(unitId);
    if (!unitData) {
      throw new Error(`해당 동호수가 존재하지 않습니다. unitId: ${unitId}`);
    }
    await this.unitModelSystem.enter(unitData, isHideUnitName);
  }

  public exitUnit() {
    this.unitModelSystem.exit();
  }

  public createUnitImmediately(unitId: number) {
    const unitData = this.danjiFetcher.getUnitData(unitId);
    if (!unitData) {
      console.error(new Error('해당 동호수가 존재하지 않습니다.'), unitId);
      return;
    }
    this.unitSystem.createUnitImmediately(unitData);
    this.unitModelSystem.generateUnitModelTransforms();
  }
}
